import React, { useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ReactTooltip from "react-tooltip";
import {
  CancelButton,
  PrimaryButtonFitContainer,
} from "../../../components/Buttons/Buttons";
import { SelectBoxV2 } from "../../../components/SelectBoxV2/SelectBoxV2";
import { TextField } from "../../../components/TextFields/TextFields";
import { SmallSectionHeader } from "../../../components/Typography/Typography";
import { endpoints } from "../../../endpoints";
import { FormGrid2x2 } from "../../../layout/FormLayout";
import type {
  CurrencyCode,
  OptionType,
  ProductSKU,
  UUID,
} from "../../../types/types";
import {
  packagingTypeOrUnitToOption,
  usePackagingUnits,
} from "../../../util/SkuUtils";
import {
  convertProductSKUToOption,
  useCurrencySymbol,
  useStoreState,
} from "../../../util/util";
import type { CustomSkuData } from "../../SharedPages/SellerQuoteDetailPage/AddCustomSkuForm";
import { SAMPLE_REQUEST_PURPOSES } from "../../public/AddToSampleRequestForm/AddToSampleRequestForm";
import { strings } from "../../../util/strings";
import { SearchSelectInfiniteScroll } from "../../../components/SearchSelectInfiniteScroll/SearchSelectInfiniteScroll";
import type { PIMProductBase } from "../../../types/types.PIM";
import { ProductApplicationSelect } from "../../../components/ProductApplicationSelect/ProductApplicationSelect";
import { ToggleSwitchV2 } from "../../../components/ToggleSwitch/ToggleSwitch";
import { KeyValueDisplay } from "../../../components/KeyValueDisplay";
import {
  calculateTotalQuantity,
  formatTotalQuantityString,
} from "../../Buyer/RequestUnlistedProduct/UnlistedQuoteItemForm";

/**
 * Heavily derived from `FormInputs` in `BuyerQuoteItemForm`
 * `price_per_uom` will only exist if price tiers don't.
 */
export type AddOrEditProductFormOutput = {
  application?: OptionType<string> | OptionType<null>;
  custom_application?: string;
  // The actual inputs in the form depend on what the user selects for packaging.
  // We put all of them here, some as optional, and then enforce things with
  // form validation. (I tried creating a stricter type but it led to too many
  // type checking errors related to react-hook-form, e.g. for default arguments.)
  packaging: OptionType<ProductSKU> | null;
  no_of_units: string;
  unit_of_measure?: OptionType<string>;
  custom_packaging_quantity?: string;
  product_id: string;
  purpose: OptionType<string>; // TODO is this nullable?
  total_annual_quantity: string;
  total_quantity_unit: OptionType<UUID>;

  enable_payments?: boolean;
  price_per_unit?: string;
};

export type InlineDisplayCustomSKU = Omit<
  CustomSkuData,
  "package_description"
> & { kind: "Buyer SKU" };

type AddOrEditProductFormProps = {
  isDisabled: boolean;
  product: PIMProductBase | null;
  setProduct: React.Dispatch<React.SetStateAction<PIMProductBase | null>>;
  isEditMode: boolean;
  onCancel: () => void;
  buyer_tenant_id: UUID;
  currencyCode: CurrencyCode;
  lockEnablePayments: boolean;
};


export function AddOrEditSampleProductForm({
  isDisabled,
  product,
  setProduct,
  isEditMode,
  currencyCode,
  onCancel,
  buyer_tenant_id,
  lockEnablePayments,
}: AddOrEditProductFormProps) {
  const methodsOfUseForm = useFormContext();
  const {
    control,
    errors,
    formState,
    register,
    setValue,
    watch,
    setError,
    clearErrors,
  } = methodsOfUseForm;
  const currencySymbol = useCurrencySymbol(currencyCode);
  const { t } = useTranslation();
  const {
    tenant_id,
    storefront_metadata: {
      generic_sku_packaging_type_id,
      enable_payments_on_sample_requests,
    },
  } = useStoreState();
  const { packagingUnits } = usePackagingUnits();
  const packagingUnitOptions = packagingUnits
    ? packagingUnits?.map(packagingTypeOrUnitToOption)
    : [];

  const { packaging, no_of_units } = watch(["packaging", "no_of_units"]);

  const totalQuantity = calculateTotalQuantity(
    packaging?.value?.package_volume,
    no_of_units
  );

  const totalQuantityString =
    totalQuantity === null
      ? "--"
      : formatTotalQuantityString(
          packaging?.value?.packaging_unit?.name,
          totalQuantity
        );

  const handleProductChange = (option: OptionType<PIMProductBase>) => {
    setValue("product_id", option.value.id);
    if (errors["product_id"]) {
      clearErrors("product_id");
    }
    if (product) {
      setValue("unit_of_measure", { value: undefined, label: undefined });
      setValue("packaging", { value: undefined, label: undefined });
      setValue("no_of_units", null);
    }
    setProduct(option.value);
  };

  const productID = watch("product_id");
  const enablePayments = watch("enable_payments");

  useEffect(() => {
    register({ name: "product_id", required: strings(t).thisIsARequiredField });
  }, [register, t]);

  const productSkuOptions =
    product &&
    product.product_skus
      .map(convertProductSKUToOption)
      .filter(
        (item) =>
          !!item.value.is_sample &&
          item.value.packaging_type.id !== generic_sku_packaging_type_id
      );

  // We don't want user to be able to change product when editing, instead they
  // should remove it from the order and create a new item.
  const productSelectIsDisabled = isDisabled || isEditMode;

  const noOptionsMessage = ({ inputValue }: { inputValue: string }) =>
    t("No sample SKUs are available for this Product");

  return (
    <>
      <>
        <SearchSelectInfiniteScroll
          name={"product_id"}
          errors={errors}
          formState={formState}
          placeholder={t("Product")}
          baseUrl={endpoints.v2_tenants_id_pim_products_summary(tenant_id)}
          params={(() => {
            const params = new URLSearchParams();
            params.append("order_by", "asc");
            params.append("published_products_only", "true");
            params.append("include_skus", "true");
            params.append("include_applications", "true");
            if (buyer_tenant_id) {
              params.append("buyer_tenant_id", buyer_tenant_id);
            }
            return params;
          })()}
          getOptions={(response: PIMProductBase[]) =>
            response.map((product) => ({ value: product, label: product.name }))
          }
          value={{
            value: product,
            label: product ? product.name : null,
          }}
          onChange={(data: OptionType<PIMProductBase>) =>
            handleProductChange(data)
          }
          isDisabled={productSelectIsDisabled}
        />
        <div>
          <Controller
            as={SelectBoxV2}
            control={control}
            isDisabled={isDisabled}
            name="packaging"
            placeholder={t("Preferred SKU")}
            options={productSkuOptions ? productSkuOptions : []}
            rules={{ required: strings(t).thisIsARequiredField }}
            errors={errors}
            formState={formState}
            noOptionsMessage={noOptionsMessage}
          />
        </div>
        <TextField
          name={"no_of_units"}
          label={t("Number of units")}
          disabled={isDisabled}
          theref={register({
            required: true,
          })}
          errors={errors}
          formState={formState}
          type="number"
        />

        <KeyValueDisplay
          data={[
            { key: t("Total Sample Quantity"), value: totalQuantityString },
          ]}
        />
        <SmallSectionHeader>{t("Potential annual usage")}</SmallSectionHeader>
        <FormGrid2x2>
          <TextField
            name={"total_annual_quantity"}
            label={t("Total Annual Quantity")}
            disabled={isDisabled}
            theref={register({
              required: true,
            })}
            errors={errors}
            formState={formState}
            type="number"
          />
          <Controller
            as={SelectBoxV2}
            control={control}
            name="total_quantity_unit"
            placeholder={t("Unit of Measure")}
            id="total_quantity_unit"
            isDisabled={isDisabled}
            options={packagingUnitOptions}
            rules={{
              required: true,
            }}
            errors={errors}
            formState={formState}
          />
        </FormGrid2x2>
        <ProductApplicationSelect
          methodsOfUseForm={methodsOfUseForm}
          applications={product?.product_applications ?? []}
        />

        {enable_payments_on_sample_requests && (
          <TextField
            name="price_per_unit"
            label={`Price (${currencySymbol}/UoM)`}
            disabled={isDisabled || !enablePayments}
            theref={register({
              required: enablePayments,
            })}
            errors={errors}
            formState={formState}
            type="number"
          />
        )}
        <Controller
          as={SelectBoxV2}
          control={control}
          name="purpose"
          placeholder={t("Purpose")}
          rules={{ required: true }}
          options={SAMPLE_REQUEST_PURPOSES}
          isDisabled={isDisabled}
          errors={errors}
          formState={formState}
        />

        {enable_payments_on_sample_requests && (
          <ToggleSwitchV2
            name="enable_payments"
            label={t("Enable Payments")}
            ref={register({
              required: false,
            })}
            defaultChecked={enable_payments_on_sample_requests}
            disabled={lockEnablePayments}
          />
        )}
      </>
      <PrimaryButtonFitContainer
        type="submit"
        onClick={() => {
          if (!productID) {
            setError("product_id", {
              type: "required",
              message: strings(t).thisIsARequiredField,
            });
          }
        }}
      >
        {isEditMode ? t("Save Changes") : t("Add product")}
      </PrimaryButtonFitContainer>
      <ReactTooltip
        delayShow={1000}
        id="price-tiers-warning"
        delayHide={500}
        effect="solid"
      />
      {isEditMode && (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <CancelButton style={{ paddingLeft: "13px" }} onClick={onCancel}>
            {t("Cancel")}
          </CancelButton>
        </div>
      )}
    </>
  );
}
